import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Plus, CalendarDays, MoreHorizontal, Trash2 } from "lucide-react";
import { AppLayout } from "@/components/app-layout";
import { LoadingOverlay } from "@/components/loading-overlay";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { NewChoreScheduleDialog } from "@/components/new-chore-schedule-dialog";
import { listChoreSchedules, deleteChoreSchedule } from "@repo/api/client";
import type { ChoreSchedule } from "@repo/api/client";
import { EventPropagationBoundary } from "@/components/event-propagation-boundary";

export function ChoreSchedulesPage() {
  const [dialogOpen, setDialogOpen] = useState(false);

  const { data: schedules = [], isPending } = useQuery({
    queryKey: ["chore-schedules"],
    queryFn: listChoreSchedules,
  });

  return (
    <LoadingOverlay isLoading={isPending}>
      <AppLayout>
        <div className="px-4 pt-6 space-y-3">
          {schedules.map((schedule) => (
            <ChoreScheduleListItem key={schedule.id} schedule={schedule} />
          ))}

          <Button onClick={() => setDialogOpen(true)} className="w-full" variant="outline">
            <Plus size={16} />
            New Chore Schedule
          </Button>
        </div>

        <NewChoreScheduleDialog open={dialogOpen} onOpenChange={setDialogOpen} />
      </AppLayout>
    </LoadingOverlay>
  );
}

function ChoreScheduleListItem({ schedule }: { schedule: ChoreSchedule }) {
  const navigate = useNavigate();

  const scheduleUrl = `/chore-schedules/${schedule.id}`;

  return (
    <Button
      asChild
      variant="outline"
      className="w-full"
      onClick={() => navigate(scheduleUrl)}
      onKeyDown={(e) => e.key === "Enter" && navigate(scheduleUrl)}
    >
      <div data-href={scheduleUrl} tabIndex={0} role="link">
        <CalendarDays size={20} className="shrink-0 text-muted-foreground" />
        <span className="flex-1 font-medium text-left">{schedule.name}</span>
        <EventPropagationBoundary filter={{ onClick: true, onKeyDown: true }}>
          <ChoreScheduleActionsMenu schedule={schedule} />
        </EventPropagationBoundary>
      </div>
    </Button>
  );
}

function ChoreScheduleActionsMenu({ schedule }: { schedule: ChoreSchedule }) {
  const [open, setOpen] = useState(false);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="-mr-2 h-8 w-8"
          onClick={(e) => e.stopPropagation()}
        >
          <MoreHorizontal size={16} />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-40 p-1" align="end">
        <DeleteChoreScheduleButton schedule={schedule} onSuccess={() => setOpen(false)} />
      </PopoverContent>
    </Popover>
  );
}

function DeleteChoreScheduleButton({
  schedule,
  onSuccess,
}: {
  schedule: ChoreSchedule;
  onSuccess: () => void;
}) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: () => deleteChoreSchedule(schedule.id),
    onSuccess: () => {
      queryClient.removeQueries({ queryKey: ["chore-schedules", schedule.id] });
      queryClient.removeQueries({ queryKey: ["chores", schedule.id] });
      queryClient.invalidateQueries({
        queryKey: ["chore-schedules"],
        exact: true,
      });
      onSuccess();
      navigate("/chore-schedules");
    },
  });

  return (
    <>
      <Button
        onClick={() => mutation.mutate()}
        disabled={mutation.isPending}
        variant="destructive"
        className="w-full"
      >
        <Trash2 size={14} />
        {mutation.isPending ? "Deleting…" : "Delete"}
      </Button>
      {mutation.isError && <p className="px-2 py-1 text-xs text-destructive">Failed to delete.</p>}
    </>
  );
}
